import { useEffect, useState } from 'react'

/**
 * Live-ticking elapsed milliseconds since `startedAt`.
 *
 * While the session is open (no `endedAt`) it advances once a second, so the
 * cockpit shows a running session's age climbing in real time. Once ended the
 * duration is fixed at `endedAt − startedAt` and no timer runs. Returns null when
 * there is no valid start — an honest "unknown", not 0.
 */
export function useElapsed(startedAt?: string, endedAt?: string): number | null {
  const start = startedAt ? Date.parse(startedAt) : NaN
  const end = endedAt ? Date.parse(endedAt) : NaN
  const running = !Number.isNaN(start) && Number.isNaN(end)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [running])
  if (Number.isNaN(start)) return null
  const stop = Number.isNaN(end) ? now : end
  return Math.max(0, stop - start)
}

/**
 * A clock that ticks, for state whose truth is a function of elapsed time.
 *
 * The activity light needs this: whether an agent is working is decided by how
 * long ago it last produced output, so a component that recomputes only when new
 * data arrives shows a verdict frozen at the instant of the last payload — and
 * the instant a payload arrives is the instant most favourable to "working".
 * One tick, read by every surface that colours a session, also guarantees the
 * list row and the pane beside it are answering as of the same moment.
 */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}
